import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function PythonLibrariesExample() {
  return (
    <>
      <Meta
        title="Vimeo Python Libraries"
        description="Examples of using the officially supported Vimeo Python libraries."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Link href={'/api/guides/start'}>Getting Started</Path.Link>
            <Path.Current>Python Library Usage</Path.Current>
          </Path>
        }
        label="Python Library Usage"
        nav={{ page: 'getting-started' }}
      >
        <Header>Python Library Examples</Header>
        <Paragraph size="2">
          This page covers the most common use cases of the Python library. Did we miss one?{' '}
          <Link variant="minimal" href="https://vimeo.com/help/contact">
            Let us know!
          </Link>
        </Paragraph>
        <Paragraph size="2">
          If you haven't made your first API call using the Python library, check out the{' '}
          <Link variant="minimal" href="/api/guides/start">
            Getting Started
          </Link>{' '}
          guide to set up your SDK.
        </Paragraph>
        <Header size="2" id="obtaining-an-access-token">
          Obtaining an access token
        </Header>
        <Paragraph size="2">
          There are multiple ways to obtain an authenticated access token. You can generate an{' '}
          access token using the My App management page, as described in our{' '}
          <Link variant="minimal" href="/api/guides/start">
            Getting Started
          </Link>{' '}
          guide, or make an API call, as found in our{' '}
          <Link variant="minimal" href="/api/authentication#obtaining-an-access-token">
            Working with Authentication
          </Link>{' '}
          guide.
        </Paragraph>
        <Paragraph size="2">
          Another way is via the Python library. Getting a bearer token via the authorization code{' '}
          method is a bit more involved. Here are the basic steps:
          <ol>
            <li>
              We send the user to a web page where they can choose to accept or reject the{' '}
              permissions we're asking for.
            </li>
            <li>
              When the user makes their selection and accepts or rejects your app, they're{' '}
              redirected to a web page specified by you.
            </li>
            <li>
              If the user authorizes your app, you can exchange the provided code for the bearer{' '}
              token.
            </li>
          </ol>
        </Paragraph>
        <Paragraph size="2">The following code demonstrates the entire process:</Paragraph>
        <CodeBlock language="python">
          {`"""This section is used to determine where to direct the user."""
v = vimeo.VimeoClient(
    key=YOUR_CLIENT_ID,
    secret=YOUR_CLIENT_SECRET
)

vimeo_authorization_url = v.auth_url(
    ['public', 'private'], #SCOPES
    'https://example.com', #REDIRECT_URL
    STATE
)

print(vimeo_authorization_url)

# STATE - An arbitrary value. We return this value to you later as a security measure.
# REDIRECT_URL - A redirect URI associated with your app. To add or review redirect URIs, access your app's information page from My Apps.

# Your application should now redirect to \`vimeo_authorization_url\`.`}
        </CodeBlock>
        <StyledNotice>
          <strong>NOTE:</strong> The <code>redirect_url</code> needs to be added to the{' '}
          <strong>Your callback URLs</strong> section in your{' '}
          <Link variant="minimal" href="/apps">
            My App
          </Link>{' '}
          settings.
        </StyledNotice>
        <CodeBlock language="python">
          {`"""This section completes the authentication for the user."""
"""Once the authorization is granted from the \`vimeo_authorization_url\`"""

# You should retrieve the code from the URL string Vimeo redirected
# to. Here, that's named \`CODE_FROM_URL\`.
# Example: https://example.com/?code=<CODE_FROM_URL>&state=<STATE>
# By taking in the CODE_FROM_URL, the following code should return an access token.

try:
    token, user, scope = v.exchange_code(CODE_FROM_URL, 'https://example.com')
    print(token)
except vimeo.auth.GrantFailed:
    # Handle the failure to get a token from the provided code and redirect.

# Store the token, scope, and any additional user data that you require in
# your database so that users don't have to re-authorize your application
# repeatedly.`}
        </CodeBlock>
        <Paragraph size="2">
          This process is straightforward in theory, but it does require a little more effort than
          the client credentials grant to make it work properly. Remember that you may ask for
          scopes that the user decides not to give you, and your application must gracefully handle{' '}
          that.
        </Paragraph>
        <Header size="2" id="making-requests">
          Making requests
        </Header>
        <Paragraph size="2">
          PyVimeo at its core is a wrapper for{' '}
          <Link variant="minimal" href="http://docs.python-requests.org/en/master/">
            Requests
          </Link>
          , which is an elegant and simple HTTP library for Python. You can interact with the
          library as you would any other object. See{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>{' '}
          for examples.
        </Paragraph>

        <GuideTable
          tableNumber={1}
          description={<span>PyVimeo Requests examples</span>}
          headers={['HTTP method', 'Example']}
          rows={[
            ['GET', <code key="get">response = v.get(uri)</code>],
            ['PATCH', <code key="patch">response = {'v.patch(uri, data={...})'}</code>],
            ['POST', <code key="post">response = {'v.post(uri, data={...})'}</code>],
            ['PUT', <code key="put">response = {'v.put(uri, data={...})'}</code>],
            ['DELETE', <code key="delete">response = v.delete(uri)</code>],
          ]}
        />

        <Header size="3" id="about-json-filtering">
          About JSON filtering
        </Header>

        <Paragraph size="2">
          The Vimeo API supports{' '}
          <Link variant="minimal" href="/api/common-formats#json-filter">
            JSON filtering
          </Link>{' '}
          to let you return only the data that you need from an API call. To utilize this with
          PyVimeo, add a{' '}
          <code>
            <b>fields</b>
          </code>{' '}
          variable into your endpoint payload, like this:
          <CodeBlock language="python">
            {'about_me = v.get(\'/me\', params={"fields": "uri,name,pictures"})'}
          </CodeBlock>
          The response returns only the{' '}
          <code>
            <b>url</b>
          </code>
          ,{' '}
          <code>
            <b>name</b>
          </code>
          , and{' '}
          <code>
            <b>pictures</b>
          </code>{' '}
          objects.
        </Paragraph>

        <Header size="2" id="uploading-videos">
          Working with uploads
        </Header>

        <Paragraph size="2">
          For a full review of the upload API, check out{' '}
          <Link href="/api/upload/videos" variant="minimal">
            Working with Video Uploads
          </Link>
          .
        </Paragraph>
        <Header size="3">Uploading a new video</Header>
        <Paragraph size="2">
          Once you have an authenticated instance of the <code>VimeoClient</code> class, uploading
          is a single function call away with the <code>upload</code> method. Internally, this
          library provides the <code>tus</code> upload and sends a local file to the server with the{' '}
          <Link variant="minimal" href="https://tus.io/">
            tus
          </Link>{' '}
          upload protocol and{' '}
          <Link variant="minimal" href="https://github.com/tus/tus-py-client">
            tus-python-client
          </Link>
          . The following code provides an example:{' '}
        </Paragraph>
        <CodeBlock language="python">video_uri = v.upload('your-filename.mp4')</CodeBlock>
        <Paragraph size="2">
          If you wish to add metadata to your video as it's being uploaded, supply a{' '}
          <code>
            <b>data</b>
          </code>{' '}
          object to <code>.upload()</code>, like this:
          <CodeBlock language="python">
            {`video_uri = v.upload(
                'your-filename.mp4',
                data={'name': 'Video title', 'description': '...'}
            )`}
          </CodeBlock>
          Alternatively, you can do this after the video has been uploaded with a PATCH call:
          <CodeBlock language="python">
            {`video_uri = v.upload('your-filename.mp4')

v.patch(video_uri, data={'name': 'Video title', 'description': '...'})`}
          </CodeBlock>
        </Paragraph>

        <Header size="3">Replacing a video source file</Header>

        <Paragraph size="2">
          Once you have an authenticated instance of the <code>VimeoClient</code> class, you can
          also replace the source file of an existing video. Here's an example:
        </Paragraph>
        <CodeBlock language="python">
          {`video_uri = v.replace(
                'video_uri',
                'your-filename.mp4'
            )`}
        </CodeBlock>

        <Header size="3">Uploading images</Header>

        <Paragraph size="2">
          Your authenticated instance of the <code>VimeoClient</code> class also enables you to
          upload pictures as long as you supply the target object (for example, the video for which
          you'd like to replace the thumbnail):
        </Paragraph>

        <CodeBlock language="python">
          {`v.upload_picture('/videos/12345', 'your-image.jpg', activate=True)`}
        </CodeBlock>
        <StyledNotice>
          <strong>NOTE:</strong> The <code>activate=True</code> in <code>v.upload_picture</code>{' '}
          sets this picture as the active one for all users. The{' '}
          <code>
            <b>activate</b>
          </code>{' '}
          keyword argument defaults to <code>false</code>, so without it you need to activate the
          picture yourself.
        </StyledNotice>

        <Header size="3">Uploading a text track</Header>

        <Paragraph size="2">
          Uploading a text track requires an authenticated instance of the <code>VimeoClient</code>{' '}
          class, the video URI of the video that the text track will be added to, the text track
          type, the text track language, and the text track filename:
        </Paragraph>

        <CodeBlock language="python">
          {`v.upload_texttrack('/videos/12345', 'captions', 'en-US', 'your-texttrack.vtt')`}
        </CodeBlock>

        <Header size="3">Additional examples</Header>

        <Paragraph size="2">
          Though we've given you a small set of Python API call examples on this page, you might
          still be looking for more. Additional examples are in our{' '}
          <Link variant="minimal" href="/api/reference">
            API Reference
          </Link>{' '}
          guide.
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
